import type { TimetableLessonLabels } from "./components/TimetableLessonButton";
import type { TimetableContentItem } from "./timetableContent";

export type TimetableType = "staff" | "room" | "class" | "student";
export type TimetableSettings = {
	teacher:
		| "none"
		| "initials"
		| "persona"
		| "firstName"
		| "firstNameAndLastInitial"
		| "fullName";
	lesson: "none" | "code" | "name" | "yearNameCode";
	room: boolean;
	attendance: boolean;
	contentOrder: TimetableContentItem[];
	colorStyle: "course" | "block" | "none";
	fullWeek: boolean;
	showCurrentTimeIndicator: boolean;
	timeLayout: "range" | "boundary";
	combineDoublePeriods: boolean;
	lessonDisplay: "all" | "single";
	layoutMode: "landscape" | "responsive";
	portraitBreakpoint: number;
};

export type TimetableProfile =
	| "staff"
	| "room"
	| "class"
	| "student-staff"
	| "student-student"
	| "student-parent";
export type TimetableVisibility = {
	studentStaff: boolean;
	studentStudent: boolean;
	studentParent: boolean;
	examStaff: boolean;
	examStudent: boolean;
	examParent: boolean;
};
export type MyTimetableSettings = {
	profiles: Partial<
		Record<
			TimetableProfile,
			{
				global: TimetableSettings;
				user: Partial<TimetableSettings>;
				effective: TimetableSettings;
			}
		>
	>;
	visibility: TimetableVisibility;
	audience: "staff" | "student" | "parent";
};
export type GlobalTimetableSettings = {
	profiles: Record<TimetableProfile, TimetableSettings>;
	visibility: TimetableVisibility;
};

export function resolveTimetableSettings(
	global: TimetableSettings,
	user: Partial<TimetableSettings> = {},
	instance: Partial<TimetableSettings> = {},
	hardcoded: Partial<TimetableSettings> = {},
): TimetableSettings {
	const customised = (values: Partial<TimetableSettings>) =>
		Object.fromEntries(
			Object.entries(values).filter(
				([, value]) => value !== null && value !== undefined,
			),
		);
	return {
		...global,
		...customised(user),
		...customised(instance),
		...customised(hardcoded),
	};
}

export function settingsLessonLabels(
	settings: TimetableSettings,
): TimetableLessonLabels {
	return {
		staffInitials: settings.teacher === "initials",
		staffPersona: settings.teacher === "persona",
		staffFirstName: settings.teacher === "firstName",
		staffFirstNameAndLastInitial:
			settings.teacher === "firstNameAndLastInitial",
		staffFullName: settings.teacher === "fullName",
		classCode: settings.lesson === "code",
		subjectName: settings.lesson === "name",
		yearAndSubjectNameAndClassCode: settings.lesson === "yearNameCode",
		roomNumber: settings.room,
		attendance: settings.attendance,
	};
}

export function isTimetableVisible(
	visibility: TimetableVisibility,
	audience: MyTimetableSettings["audience"],
	exams = false,
) {
	const suffix = audience.charAt(0).toUpperCase() + audience.slice(1);
	return visibility[
		`${exams ? "exam" : "student"}${suffix}` as keyof TimetableVisibility
	];
}
